// IMPORTANT:
// Make sure you check email rendering after making changes in this file!
// /bin/rails r scripts/message_replay.rb replay --account YOUR_ACCOUNT_ID

import DOMPurify from 'dompurify';

// Quote detection strategies
const QUOTE_INDICATORS = [
  '#gmail_quote',
  '.gmail_quote_container',
  '.gmail_quote',
  '.gmail_extra',
  '.OutlookQuote',
  '.email-quote',
  '.quoted-text',
  '.quote',
  '[class*="quote"]',
  '[class*="Quote"]',
  '.yahoo_quoted',
  'blockquote',
  'hr',
  'div[style*="border-top"]>p[class*=Mso]',
];

// Regex patterns for quote identification
const QUOTE_PATTERNS = [
  /-{4,}.*-{4,}/,
  /^_{4,}$/,
  /^>?\s*On.+wrote:/,
  /^>?\s*Dňa.+napísal(\/a)?:/,
  /^>?\s*Dne.+napsal(\/a)?:/,
  /^>?\s*Sent:/,
  /^>?\s*From:/,
  /^>?\s*Od:/,
  /^>?\s*Gesendet:/,
];

const BLOCK_ELEMENTS = ['DIV', 'P', 'BLOCKQUOTE', 'SECTION'];

export class EmailQuoteExtractor {
  /**
   * Remove quotes from email HTML and return cleaned HTML
   * @param {string} htmlContent - Full HTML content of the email
   * @param {number} skipFirstMatches - Number of first matches to skip
   * @returns {string|null} HTML content with quotes removed
   */
  static extractQuotes(htmlContent, skipFirstMatches = 0) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(htmlContent);

    const signatureElement = tempDiv.querySelector(
      '[id*="html_email_signature"]'
    );
    if (signatureElement) {
      signatureElement.remove();
    }

    const { quoteElements, matchFound, skippedElement } =
      this.findQuoteElements(tempDiv, skipFirstMatches);

    quoteElements.forEach(el => {
      el.remove();
    });

    const { quoteBlocks, headers } = this.findTextNodeQuotes(
      tempDiv,
      skippedElement
    );
    quoteBlocks.forEach(el => {
      el.remove();
    });

    this.removeBlankElements(tempDiv);
    this.removeEmptyLines(tempDiv);
    this.setParagraphMargins(tempDiv);

    let cleanedHTML = tempDiv.innerHTML;

    if (skipFirstMatches > 0 && headers) {
      cleanedHTML = headers + cleanedHTML;
    }
    const textContent = tempDiv.textContent?.trim() || '';

    if (textContent === '' && matchFound && skipFirstMatches === 0) {
      return this.extractQuotes(htmlContent, 1);
    }

    if (textContent === '') {
      return null;
    }

    return cleanedHTML;
  }

  /**
   * Find elements that match quote indicators and all following elements
   * @param {Element} rootElement - Root element to search
   * @param {number} skipFirstMatches - Number of first matches to skip
   * @returns {Object} Object with quoteElements array, matchFound boolean, and skippedElement
   */
  static findQuoteElements(rootElement, skipFirstMatches = 0) {
    const quoteElements = [];
    const treeWalker = document.createTreeWalker(
      rootElement,
      NodeFilter.SHOW_ELEMENT,
      null,
      false
    );

    let quoteDetected = false;
    let matchesSkipped = 0;
    let matchFound = false;
    let skippedElement = null;

    for (
      let currentNode = treeWalker.nextNode();
      currentNode !== null;
      currentNode = treeWalker.nextNode()
    ) {
      if (quoteDetected) {
        quoteElements.push(currentNode);
      } else {
        const matchesQuoteIndicator = QUOTE_INDICATORS.find(selector => {
          return currentNode.matches(selector);
        });

        if (matchesQuoteIndicator) {
          matchFound = true;

          if (matchesSkipped < skipFirstMatches) {
            matchesSkipped += 1;
            skippedElement = currentNode;
          } else {
            quoteDetected = true;
            quoteElements.push(currentNode);
          }
        }
      }
    }

    return { quoteElements, matchFound, skippedElement };
  }

  /**
   * Get direct text content of an element (not including child elements)
   * @param {Element} element - Element to get direct text from
   * @returns {string} Direct text content
   */
  static getDirectTextContent(element) {
    return Array.from(element.childNodes)
      .filter(node => node.nodeType === Node.TEXT_NODE)
      .map(node => node.textContent)
      .join('');
  }

  /**
   * Check if an element is inside or is the skipped element
   * @param {Element} element - Element to check
   * @param {Element|null} skippedElement - Element to check against
   * @returns {boolean} True if element is inside skippedElement
   */
  static isInsideSkippedElement(element, skippedElement) {
    if (!skippedElement) return false;
    if (element === skippedElement) return true;
    return skippedElement.contains(element);
  }

  /**
   * Find text nodes that match quote patterns
   * @param {Element} rootElement - Root element to search
   * @param {number} skipFirstMatches - Number of first matches to skip (for retry mode)
   * @param {Element|null} skippedElement - Element to skip searching inside
   * @returns {Object} Object with quoteBlocks array and headers string
   */
  static findTextNodeQuotes(rootElement, skippedElement = null) {
    const quoteBlocks = [];
    const treeWalker = document.createTreeWalker(
      rootElement,
      NodeFilter.SHOW_ELEMENT,
      null,
      false
    );

    let quoteDetected = false;
    let headers = null;

    for (
      let currentNode = treeWalker.nextNode();
      currentNode !== null;
      currentNode = treeWalker.nextNode()
    ) {
      if (!this.isInsideSkippedElement(currentNode, skippedElement)) {
        if (quoteDetected) {
          if (BLOCK_ELEMENTS.includes(currentNode.tagName)) {
            quoteBlocks.push(currentNode);
          }
        } else {
          const directText = this.getDirectTextContent(currentNode).trim();

          if (directText) {
            const isQuoteLike = QUOTE_PATTERNS.find(pattern =>
              pattern.test(directText)
            );

            if (isQuoteLike) {
              quoteDetected = true;

              const parentBlock = this.findParentBlock(currentNode);
              if (parentBlock && !quoteBlocks.includes(parentBlock)) {
                const wasSplit = this.splitBlockAtQuote(
                  parentBlock,
                  currentNode
                );
                if (!wasSplit) {
                  quoteBlocks.push(parentBlock);
                }
              }
            }
          }
        }
      }
    }

    return { quoteBlocks, headers };
  }

  /**
   * Find the closest block-level parent element by recursively traversing up the DOM tree.
   * This method searches for common block-level elements like DIV, P, BLOCKQUOTE, and SECTION
   * that contain the text node. It's used to identify and remove entire block-level elements
   * that contain quote-like text, rather than just removing the text node itself. This ensures
   * proper structural removal of quoted content while maintaining HTML integrity.
   * @param {Node} node - Starting node to find parent
   * @returns {Element|null} Block-level parent element
   */
  /**
   * Split a block element at the point where a quote pattern is found.
   * Finds which direct child of blockElement contains the quoteNode,
   * then checks if there is meaningful content before it. If yes, removes
   * everything from that child onwards and returns true (block was split).
   * If the match is at the start, returns false (caller should remove whole block).
   * @param {Element} blockElement - Block element to split
   * @param {Element} quoteNode - The element/node that triggered the quote match
   * @returns {boolean} True if block was split (caller should NOT remove it)
   */
  static splitBlockAtQuote(blockElement, quoteNode) {
    const childNodes = Array.from(blockElement.childNodes);
    let matchIndex = -1;

    // Find which direct child of blockElement contains (or is) the quoteNode
    for (let i = 0; i < childNodes.length; i += 1) {
      const child = childNodes[i];
      if (child === quoteNode || child.contains?.(quoteNode)) {
        matchIndex = i;
        break;
      }
      // Also check direct text nodes for quote patterns
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent.trim();
        if (text && QUOTE_PATTERNS.some(pattern => pattern.test(text))) {
          matchIndex = i;
          break;
        }
      }
    }

    if (matchIndex === -1) return false;

    // Check if there is meaningful content before the match
    const hasPrecedingContent = childNodes.slice(0, matchIndex).some(node => {
      const text = node.textContent.replace(/[\s\u00a0]/g, '');
      return text.length > 0;
    });

    if (!hasPrecedingContent) return false;

    // Remove the matching child and all subsequent siblings
    for (let i = childNodes.length - 1; i >= matchIndex; i -= 1) {
      childNodes[i].remove();
    }

    return true;
  }

  static findParentBlock(node) {
    let current = node;

    while (current) {
      if (BLOCK_ELEMENTS.includes(current.tagName)) {
        return current;
      }
      current = current.parentElement;
    }

    return null;
  }

  /**
   * Remove blank elements from the DOM tree
   * @param {Element} rootElement - Root element to clean
   */
  static removeBlankElements(rootElement) {
    const elementsToRemove = [];
    const treeWalker = document.createTreeWalker(
      rootElement,
      NodeFilter.SHOW_ELEMENT,
      null,
      false
    );

    const SELF_CLOSING_TAGS = ['BR', 'IMG'];

    for (
      let currentNode = treeWalker.nextNode();
      currentNode !== null;
      currentNode = treeWalker.nextNode()
    ) {
      const textContent = currentNode.textContent?.trim() || '';
      const tagName = currentNode.tagName;

      if (
        textContent === '' &&
        tagName !== 'P' &&
        !SELF_CLOSING_TAGS.includes(tagName)
      ) {
        elementsToRemove.push(currentNode);
      }
    }

    elementsToRemove.forEach(el => el.remove());
  }

  /**
   * Remove empty lines from the HTML content
   * @param {Element} rootElement - Root element to clean
   */
  static removeEmptyLines(rootElement) {
    const elementsToRemove = [];
    const treeWalker = document.createTreeWalker(
      rootElement,
      NodeFilter.SHOW_ELEMENT,
      null,
      false
    );

    for (
      let currentNode = treeWalker.nextNode();
      currentNode !== null;
      currentNode = treeWalker.nextNode()
    ) {
      const textContent = currentNode.textContent || '';
      const innerHTML = currentNode.innerHTML || '';
      const isEmptyLine =
        textContent.trim() === '' &&
        innerHTML.trim() !== '' &&
        (innerHTML.match(/^[\s\n\r&nbsp;]*$/i) || innerHTML === '');

      if (isEmptyLine && currentNode.tagName !== 'IMG') {
        elementsToRemove.push(currentNode);
      }
    }

    elementsToRemove.forEach(el => el.remove());
  }

  /**
   * @param {Element} rootElement - Root element to modify
   */
  static setParagraphMargins(rootElement) {
    const paragraphs = rootElement.querySelectorAll('p');
    paragraphs.forEach(p => {
      const hasMsoClass = Array.from(p.classList).some(cls =>
        cls.startsWith('Mso')
      );

      if (hasMsoClass) {
        p.style.marginBottom = '0px';
      } else {
        p.style.marginBottom = '0.5rem';
      }
    });
  }
}
